import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Alert, Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import theme from './theme';
import './App.css';

import Navbar from './components/Navbar.js';
import BottomNav, { BOTTOM_NAV_HEIGHT } from './components/BottomNav';
import { CONTENT_MAX_WIDTH } from './theme';
import Schedule from './pages/Schedule.js';
import Standings from './pages/Standings.js';
import Analytics from './pages/Analytics.js';
import MyTeam from './pages/MyTeam.js';
import TeamDetail from './pages/TeamDetail.js';
import Admin from './pages/Admin.js';
import UpdatePrompt from './components/UpdatePrompt.js';
import useOnlineStatus from './hooks/useOnlineStatus';
import { trackPageView } from './utils/analytics';
// Lazily loaded: it pulls in Leaflet, and there is no reason for every visitor to
// download a map library for a screen only an administrator opens.
const AdminVenues = React.lazy(() => import('./pages/AdminVenues.js'));
const Venue = React.lazy(() => import('./pages/Venue.js'));

// Theme and design tokens live in src/theme.js

// Component to track page views on route changes
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const title = path === '/' ? 'Schedule' : path.charAt(1).toUpperCase() + path.slice(2);
    trackPageView(path, title);
  }, [location]);

  return null;
}

function App() {
  const isOnline = useOnlineStatus();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Box
          className="App"
          sx={{
            pb: {
              xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`,
              md: 0,
            },
          }}
        >
          <Navbar />
          <PageViewTracker />
          <UpdatePrompt />
          {!isOnline && (
            <Alert severity="warning" sx={{ borderRadius: 0 }}>
              You are offline. Showing cached data.
            </Alert>
          )}
          {/* The content column, and the one place the page gutter is defined.
              Navbar uses the same maxWidth and the same px, so the brand and the
              page heading start on the same line. This Box used to set no padding
              at all, which left every page to invent its own: the filter band
              carried a matching mx, two pages used p:4, and Venue had none and
              sat 24px left of the header. */}
          <Box sx={{
            maxWidth: CONTENT_MAX_WIDTH,
            mx: 'auto',
            width: '100%',
            px: { xs: 1.5, md: 3 },
          }}>
          <Routes>
            <Route path="/" element={<Schedule />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/standings" element={<Standings />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/my-team" element={<MyTeam />} />
            <Route path="/team/:teamId" element={<TeamDetail />} />
            <Route
              path="/venue/:venueId"
              element={(
                <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}>
                  <Venue />
                </React.Suspense>
              )}
            />
            <Route path="/admin" element={<Admin />} />
            <Route
              path="/admin/venues"
              element={(
                <React.Suspense fallback={<Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>}>
                  <AdminVenues />
                </React.Suspense>
              )}
            />
          </Routes>
          </Box>
          <BottomNav />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
